import React from "react";
import { css } from "@emotion/react";
import { StaticImage } from "gatsby-plugin-image";

import Link from "../components/link";
import ColorDivider from "../components/colorDivider";

import { baseColor1, baseColor2, lightBaseColor1 } from "../styles/color";
import {
  breakpointLg,
  breakpointMd,
  breakpointSm,
  breakpointXl,
} from "../styles/layout";

import MulticolorLogo from "../images/one-work-multicolor-logo.svg";

const footerStyles = css`
  background-color: ${baseColor2};
  width: 100%;
  display: grid;
  justify-content: center;

  .footer-content {
    display: grid;
    grid-template-columns: auto auto;
    padding: 2.5rem 1rem 2rem 1rem;
  }

  .address,
  .copyright > p {
    color: ${baseColor1};
    font-size: 1rem;
  }

  .copyright {
    grid-column: 1 / 3;
    display: grid;
    grid-row-gap: 1rem;
  }

  .social {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 1rem;

    & > div {
      display: inline-block;
    }
  }

  .footer-links {
    margin: 1.5rem 0;
    grid-column: 1 / 3;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 1.5rem;
    & > * {
      display: inline-block;
      font-size: 0.85rem;
      color: ${lightBaseColor1};
      text-decoration: none;
    }
    & > *:hover {
      color: ${baseColor1};
    }
  }

  @media only screen and (min-width: ${breakpointSm}) {
    .footer-content {
      padding: 2.5rem 0 2rem 0;
      width: calc(100vw - 4rem);
    }
  }

  @media only screen and (min-width: ${breakpointMd}) {
    .footer-content {
      width: 840px;
    }

    .copyright {
      text-align: center;
    }
  }

  @media only screen and (min-width: ${breakpointLg}) {
    .footer-content {
      width: calc(100vw - 400px);
    }
  }

  @media only screen and (min-width: ${breakpointXl}) {
    .footer-content {
      width: 1040px;
    }
  }
`;

const Footer = () => {
  return (
    <>
      <ColorDivider />
      <section css={footerStyles}>
        <div className="footer-content">
          <div>
            <Link to="/">
              <img
                src={MulticolorLogo}
                alt="the multicolor One Work logo"
                height={48}
                css={css`
                  margin-bottom: 1rem;
                `}
              />
            </Link>
            <p className="address">
              802 E Whiting St
              <br />
              Tampa, FL 33602
            </p>
          </div>
          <div className="social">
            <div>
              <a
                href="https://twitter.com/joinonework"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StaticImage
                  src="../images/twitter-white.svg"
                  alt="Twitter logo"
                  width={24}
                  objectFit="contain"
                />
              </a>
            </div>
            <div>
              <a
                href="https://www.instagram.com/joinonework"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StaticImage
                  src="../images/instagram-white.svg"
                  alt="Instagram logo"
                  width={24}
                />
              </a>
            </div>
            <div>
              <a
                href="https://www.facebook.com/joinonework"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StaticImage
                  src="../images/facebook-white.svg"
                  alt="Facebook logo"
                  width={24}
                />
              </a>
            </div>
            <div>
              <a
                href="https://www.linkedin.com/company/joinonework"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StaticImage
                  src="../images/linkedin-white.png"
                  alt="LinkedIn logo"
                  width={24}
                />
              </a>
            </div>
          </div>
          <div className="footer-links">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/cookie-policy">Cookie Policy</Link>
            <Link to="/acceptable-use-policy">Acceptable Use Policy</Link>
          </div>
          <div className="copyright">
            <p>
              <span role="img" aria-label="pride flag">
                🏳️‍🌈
              </span>{" "}
              We're an LGBT-owned{" "}
              <Link
                external
                to="https://benefitcorp.net/faq"
                color={baseColor1}
              >
                public benefit corporation
              </Link>
              , and we think that's simply marvelous — like you!
            </p>
            <p>
              © 2022 One Work Co. The One Work name and logo are trademarks of
              One Work Co. All rights reserved.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Footer;
